import React, {useEffect} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {ControlButton, Glyphs, TscButtonType, TscThemeNames, TscThemeName, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import SnagitLogoSvg from '../../static/svg/SnagitLogo24x24.svg';
import CamtasiaLogoSvg from '../../static/svg/CamtasiaLogo.svg';
import CssUtil from '../../util/CssUtil';
import cssConstants from '../../constants/cssConstants';
import Constants from '../../constants/Constants';
import Analytics, {IUaEventOptions, IUAEventParams} from '../../service/analytics';
import {mediaStore} from '../../context/mediaProvider';
import {
   showEmbeddedCamtasiaMarketing,
   showEmbeddedCamtasiaWebMarketing,
   showEmbeddedCaptureMarketing,
   showEmbeddedSnagitMarketing,
   showEmbeddedWebUploadMarketing
} from '../../util/MarketingHelper';

const backgroundColorOpacity = 0.95;

const MarketingEmbedPlugin = styled.div<{theme: TscThemeNames}>`
   position: absolute;
   top: 1rem;
   left: 1rem;
   display: flex;
   border-radius: ${TscStyles.border.radius.md};
   -webkit-box-align: center;
   align-items: center;
   height: 2rem;
   font-family: ${TscStyles.font.family.primary};
   background-color: ${props => props.theme === TscThemeName.dusk ? CssUtil.toRgba(TscStyles.color.ui.dusk.medium, backgroundColorOpacity) : CssUtil.toRgba(TscStyles.color.ui.dawn.light, backgroundColorOpacity)};
   box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   pointer-events: auto;

   @media screen and (min-width: ${cssConstants.smartPlayerMinWidth}) {
      height: 2.5rem;
      border-radius: ${TscStyles.border.radius.lg};
   }
`;

const CloseButton = styled.span`
   margin: 0;

   @media screen and (min-width: ${cssConstants.smartPlayerMinWidth}) {
      margin: 0 .25rem 0 0;
   }
`;

const IconSvg = styled.span`
   display: flex;
   margin: 0.5rem;

   svg {
      height: 1rem;
      width: 1rem;
   }

   @media screen and (min-width: ${cssConstants.smartPlayerMinWidth}) {
      margin: .5rem .5rem .5rem 0.75rem;

      svg {
         height: 1.5rem;
         width: 1.5rem;
      }
   }
`;

const IconText = styled.button<{theme: TscThemeNames; displayCloseButton: boolean; linked: boolean}>`
   display: inline-block;
   margin-right: ${props => props.displayCloseButton ? '0.25rem' : '.5rem'};
   padding: 0;
   background: none;
   font-weight: 600;
   font-size: 0.875rem;
   font-family: Figtree, sans-serif;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dawn.light : TscStyles.color.ui.dusk.medium};
   cursor: ${props => props.linked ? 'pointer' : 'auto'};
   border: none;

   @media screen and (min-width: ${cssConstants.smartPlayerMinWidth}) {
      font-size: 1rem;
      margin-right: ${props => props.displayCloseButton ? '0.25rem' : '1rem'};
   }
`;

export const MarketingEmbedPluginBase: React.FC<IStateMappedProps> = ({canClose, linkToMoreInfo, onClose, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const isSnagitPlugin = showEmbeddedSnagitMarketing();
   const isCamtasiaPlugin = showEmbeddedCamtasiaMarketing();
   const isCamtasiaWebPlugin = showEmbeddedCamtasiaWebMarketing();
   const isCapturePlugin = showEmbeddedCaptureMarketing();
   const isWebUploadsPlugin = showEmbeddedWebUploadMarketing();

   const recordGAEmbeddedBannerButtonClickedEvent = () => {
      const eventName = isSnagitPlugin ? Constants.analytics.SnagitEmbeddedButtonClicked : isCamtasiaPlugin ? Constants.analytics.CamtasiaEmbeddedButtonClicked : isCamtasiaWebPlugin ? Constants.analytics.CamtasiaWebEmbeddedButtonClicked : isCapturePlugin ? Constants.analytics.CaptureEmbeddedButtonClicked : isWebUploadsPlugin ? Constants.analytics.WebUploadEmbeddedButtonClicked : null;
      /* eslint-disable camelcase */
      const ga4Event: IUaEventOptions = {
         eventName: eventName,
         eventParams: {
            banner_variant: '1',
            media_hash: Hash
         } as IUAEventParams
      };
      /* eslint-enable */

      Analytics.gaEvent(ga4Event);
   };

   const recordGABannerDisplayedEvent = () => {
      const eventName = isSnagitPlugin ? Constants.analytics.SnagitEmbeddedBannerDisplayed : isCamtasiaPlugin ? Constants.analytics.CamtasiaEmbeddedBannerDisplayed : isCamtasiaWebPlugin ? Constants.analytics.CamtasiaWebEmbeddedBannerDisplayed : isCapturePlugin ? Constants.analytics.CaptureEmbeddedBannerDisplayed : isWebUploadsPlugin ? Constants.analytics.WebUploadEmbeddedBannerDisplayed : null;
      /* eslint-disable camelcase */
      const ga4Event: IUaEventOptions = {
         eventName: eventName,
         eventParams: {
            banner_variant: '1',
            media_hash: Hash
         } as IUAEventParams
      };
      /* eslint-enable */

      Analytics.gaEvent(ga4Event);
   };

   useEffect(() => {
      recordGABannerDisplayedEvent();
   }, []);

   const navigateToMarketingPage = () => {
      window.open(
         (isSnagitPlugin || isCapturePlugin || isWebUploadsPlugin) ? Constants.navigation.banners.screenCapture : isCamtasiaWebPlugin ? Constants.navigation.banners.camtasiaWeb : Constants.navigation.banners.camtasiaProduct,
         '_blank'
      );
      recordGAEmbeddedBannerButtonClickedEvent();
   };

   return (
      <MarketingEmbedPlugin theme={theme}>
         <IconSvg>
            {(isSnagitPlugin || isCapturePlugin || isWebUploadsPlugin) && <SnagitLogoSvg />}
            {isCamtasiaPlugin || isCamtasiaWebPlugin && <CamtasiaLogoSvg />}
         </IconSvg>
         <IconText
            theme={theme}
            displayCloseButton={canClose}
            linked={linkToMoreInfo}
            onClick={linkToMoreInfo ? navigateToMarketingPage : null}
         >
            {isSnagitPlugin && t('marketing.snagit.embedText')}
            {isCamtasiaPlugin && t('marketing.camtasia.embedText')}
            {isCamtasiaWebPlugin && t('marketing.camtasiaweb.embedText')}
            {isCapturePlugin && t('marketing.capture.embedText')}
            {isWebUploadsPlugin && t('marketing.webupload.embedText')}
         </IconText>
         {canClose && (
            <CloseButton>
               <ControlButton
                  glyph={<Glyphs.Close16x16 />}
                  buttonType={TscButtonType.tertiary}
                  themeName={theme}
                  title={t('marketing.closeEmbeddedMarketingPaneAccessibleLabel')}
                  testId="hide-media-tabs-button"
                  ariaLabel={t('marketing.closeEmbeddedMarketingPaneAccessibleLabel')}
                  onClick={onClose}
               />
            </CloseButton>
         ) }
      </MarketingEmbedPlugin>
   );
};

export interface IStateMappedProps {
   canClose: boolean;
   linkToMoreInfo: boolean;
   onClose: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
   Hash: string;
}

const mapStatesToProps = (
   {canClose, linkToMoreInfo, onClose}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps => ({canClose, linkToMoreInfo, onClose, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MarketingEmbedPluginBase);
